// =============================================================================
// TimeZoneNotice - Avviso quando l'operatore non è nel fuso dell'organizzazione
// =============================================================================
// Il calendario mostra sempre l'ora dell'ATTIVITÀ: due colleghi in fusi diversi
// devono leggere lo stesso orario per lo stesso appuntamento. Ma chi guarda da
// un altro fuso ha bisogno di sapere che le "9:00" non sono le sue 9:00, e a
// volte di sapere che ora sono davvero da lui. Da qui banner + toggle.
//
// Non compare quando i due fusi mostrano lo stesso orario: per la stragrande
// maggioranza degli utenti sarebbe solo rumore.
// =============================================================================

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTenantTimeZone } from '../../../store/tenantStore';
import {
  useSchedulerStore,
  useShowViewerLocalTimes,
} from '../../../store/schedulerStore';
import {
  browserTimeZone,
  timeZoneAbbreviation,
  timeZoneLabel,
  timeZonesDiffer,
} from '../../../lib/tenantTime';

export function TimeZoneNotice() {
  const { t } = useTranslation('scheduler');
  const tenantTimeZone = useTenantTimeZone();
  const showViewerLocal = useShowViewerLocalTimes();
  const setShowViewerLocalTimes = useSchedulerStore((s) => s.setShowViewerLocalTimes);

  const viewerTimeZone = useMemo(() => browserTimeZone(), []);
  const differs = useMemo(
    () => timeZonesDiffer(tenantTimeZone, viewerTimeZone),
    [tenantTimeZone, viewerTimeZone],
  );

  if (!differs) return null;

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
      <div className="flex items-center gap-2 min-w-0">
        <Globe className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <p className="text-xs text-amber-900 truncate">
          {t('timeZone.notice', {
            tenantZone: timeZoneLabel(tenantTimeZone),
            tenantAbbr: timeZoneAbbreviation(tenantTimeZone),
            viewerZone: timeZoneLabel(viewerTimeZone),
          })}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setShowViewerLocalTimes(!showViewerLocal)}
        aria-pressed={showViewerLocal}
        className={cn(
          'flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
          showViewerLocal
            ? 'bg-amber-600 text-white hover:bg-amber-700'
            : 'bg-white text-amber-800 border border-amber-300 hover:bg-amber-100',
        )}
      >
        {showViewerLocal ? t('timeZone.hideLocal') : t('timeZone.showLocal')}
      </button>
    </div>
  );
}
